import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { Mandant } from '../../../../../projects/kiene-core/src/lib/components/mandanten/classes/mandant';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { SaveForAllDialogComponent } from '../../../dialogs/save-for-all-dialog/save-for-all-dialog.component';

@Component({
    selector: 'kiene-nebenkosten-artikel-dialog',
    templateUrl: './nebenkosten-artikel-dialog.component.html',
    styleUrls: ['./nebenkosten-artikel-dialog.component.scss'],
})
export class NebenkostenArtikelDialogComponent implements OnInit, OnDestroy {
    bezeichnungCtrl = new UntypedFormControl('', [Validators.required]);
    kuerzelCtrl = new UntypedFormControl('');
    buchungskontoCtrl = new UntypedFormControl('', [Validators.required]);
    buchungsgegenkontoCtrl = new UntypedFormControl('1600');
    buchungskontoOptions: { konto_nr: string; bezeichnung: string }[] = [];
    gegenkontoOptions: { konto_nr: string; bezeichnung: string }[] = [];
    saving = false;

    private kuerzelTouched = false;
    private kontoSubs: Subscription[] = [];

    constructor(
        public dialogRef: MatDialogRef<NebenkostenArtikelDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: unknown,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private messageService: MessageService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.bezeichnungCtrl.valueChanges.subscribe((v: string) => {
            if (this.kuerzelTouched) {
                return;
            }
            const bez = (v || '').trim();
            if (!bez) {
                this.kuerzelCtrl.setValue('', { emitEvent: false });
                return;
            }
            const first = bez.split(/\s+/)[0] || bez;
            this.kuerzelCtrl.setValue(first.substring(0, 12), { emitEvent: false });
        });
        this.kuerzelCtrl.valueChanges.subscribe(() => {
            this.kuerzelTouched = true;
        });
        this.wireSachkontoSearch(this.buchungskontoCtrl, opts => (this.buchungskontoOptions = opts));
        this.wireSachkontoSearch(this.buchungsgegenkontoCtrl, opts => (this.gegenkontoOptions = opts));
    }

    ngOnDestroy(): void {
        this.kontoSubs.forEach(s => s.unsubscribe());
    }

    isValid(): boolean {
        return this.bezeichnungCtrl.valid && this.buchungskontoCtrl.valid;
    }

    save() {
        if (!this.isValid() || this.saving) {
            return;
        }
        const body: any = {
            bezeichnung: (this.bezeichnungCtrl.value || '').trim(),
            kuerzel: (this.kuerzelCtrl.value || '').trim() || null,
            buchungskonto: (this.buchungskontoCtrl.value || '').trim(),
            buchungsgegenkonto: (this.buchungsgegenkontoCtrl.value || '').trim() || '1600',
            einheit_id: 9,
            ust: 19,
            honorar: 0,
        };

        this.saving = true;
        this.api.get(this.apiBaseUrl + 'fibu_nebenkosten_artikel/read_mandanten.php').subscribe({
            next: (response: any) => {
                const available = this.toMandanten(response?.records ?? []);
                if (available.length <= 1) {
                    this.postCreate(body);
                    return;
                }
                this.saving = false;
                const dialogRef = this.dialog.open(SaveForAllDialogComponent, {
                    width: '520px',
                    data: {
                        availableMandanten: available,
                        buttonText: 'Anlegen',
                    },
                });
                dialogRef.afterClosed().subscribe((result: any) => {
                    if (!result) {
                        return;
                    }
                    const ids = Array.isArray(result) ? result : result.mandanten_ids || [];
                    if (!ids.length) {
                        return;
                    }
                    body.mandanten_ids = ids;
                    this.saving = true;
                    this.postCreate(body);
                });
            },
            error: () => this.postCreate(body),
        });
    }

    close() {
        this.dialogRef.close(false);
    }

    private wireSachkontoSearch(
        ctrl: UntypedFormControl,
        assign: (opts: { konto_nr: string; bezeichnung: string }[]) => void,
    ): void {
        const sub = ctrl.valueChanges
            .pipe(
                debounceTime(220),
                distinctUntilChanged(),
                switchMap(raw => {
                    const term = String(raw || '').trim();
                    if (term.length < 1) {
                        assign([]);
                        return of(null);
                    }
                    const params = new HttpParams()
                        .set('search', term)
                        .set('aktiv', '1')
                        .set('limit', '40')
                        .set('offset', '0');
                    return this.api.get(this.apiBaseUrl + 'fibu_konto/read.php', params).pipe(
                        catchError(() => {
                            assign([]);
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe(res => {
                if (!res) {
                    return;
                }
                const records = Array.isArray(res?.records) ? res.records : [];
                assign(
                    records
                        .filter((r: any) => {
                            const art = String(r.konto_art || 'SACHKONTO').toUpperCase();
                            return art === 'SACHKONTO' || art === '';
                        })
                        .map((r: any) => ({
                            konto_nr: String(r.konto_nr || ''),
                            bezeichnung: String(r.bezeichnung || ''),
                        })),
                );
            });
        this.kontoSubs.push(sub);
    }

    private postCreate(body: any): void {
        this.api.post(this.apiBaseUrl + 'fibu_nebenkosten_artikel/create.php', body).subscribe({
            next: created => {
                this.saving = false;
                this.messageService.infoMessage('Nebenkosten-Artikel angelegt.');
                this.dialogRef.close(created || true);
            },
            error: err => {
                this.saving = false;
                this.messageService.errorMessage('Anlegen fehlgeschlagen.', err);
            },
        });
    }

    private toMandanten(records: any[]): Mandant[] {
        return (records || [])
            .map(r => {
                const m = new Mandant();
                m.mandant_id = Number(r.mandant_id);
                m.bezeichnung = r.bezeichnung || '';
                return m;
            })
            .filter(m => m.mandant_id > 0);
    }
}
